/**
 * PositFiltersDropdown.tsx
 * 
 * Filter dropdown component for the Posits listing.
 * Extracted from MyPosits.tsx to improve modularity and reusability.
 */

import React from "react";
import { Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PositFiltersDropdownProps {
  filterType: string;
  setFilterType: (value: string) => void;
  filterPrivacy: string;
  setFilterPrivacy: (value: string) => void;
  showFilters: boolean;
  setShowFilters: (value: boolean) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

const PositFiltersDropdown: React.FC<PositFiltersDropdownProps> = ({
  filterType,
  setFilterType,
  filterPrivacy,
  setFilterPrivacy,
  showFilters,
  setShowFilters,
  clearFilters,
  hasActiveFilters,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={hasActiveFilters ? "default" : "outline"}
          size="sm"
          className="h-9"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="mr-2 h-4 w-4" />
          Filters
          {hasActiveFilters && (
            <span className="ml-1 rounded-full bg-primary-foreground text-xs w-5 h-5 flex items-center justify-center">
              {(filterType !== "all" ? 1 : 0) + (filterPrivacy !== "all" ? 1 : 0)}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[280px]">
        <DropdownMenuLabel>Filter Posits</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup className="p-2 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="type-filter">
              Type
            </label>
            <Select
              value={filterType}
              onValueChange={setFilterType}
            >
              <SelectTrigger id="type-filter" className="w-full">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="decision_tracking">Decision Tracking</SelectItem>
                <SelectItem value="process_tracking">Process Tracking</SelectItem>
                <SelectItem value="learnings_tracking">Learnings Tracking</SelectItem>
                <SelectItem value="user_knowledge_base">User Knowledge Base</SelectItem>
                <SelectItem value="second_brain">Second Brain</SelectItem>
                <SelectItem value="knowledge_base">Knowledge Base</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="privacy-filter">
              Privacy
            </label>
            <Select
              value={filterPrivacy}
              onValueChange={setFilterPrivacy}
            >
              <SelectTrigger id="privacy-filter" className="w-full">
                <SelectValue placeholder="Select privacy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="exclusive">Exclusive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {hasActiveFilters && (
            <Button
              variant="ghost"
              className="w-full h-8 text-xs mt-2"
              onClick={clearFilters}
            >
              <X className="mr-2 h-3 w-3" />
              Clear Filters
            </Button>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default PositFiltersDropdown; 